import { Injectable, computed, inject, signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { StorageManager } from '../../infrastructure/managers/storage.manager';
import { Tenant, TenantRole } from '../interfaces';
import { TenantService } from '../services/tenant.service';

@Injectable({ providedIn: 'root' })
export class TenantStore {
  private readonly tenantService = inject(TenantService);
  private readonly storage = inject(StorageManager);

  readonly tenants = signal<readonly Tenant[]>([]);
  readonly activeTenantUuid = signal<string | null>(null);
  readonly loading = signal<boolean>(false);
  readonly error = signal<unknown>(null);

  readonly hasMultipleTenants = computed(() => this.tenants().length > 1);

  readonly activeTenant = computed(() => {
    const uuid = this.activeTenantUuid();
    if (uuid === null) {
      return null;
    }

    return this.tenants().find((tenant) => tenant.uuid === uuid) ?? null;
  });

  readonly activeTenantName = computed(() => this.activeTenant()?.name ?? '');

  private readonly rolesByTenantUuid = computed(
    () => new Map(this.tenants().map((tenant) => [tenant.uuid, new Set(tenant.roles)]))
  );

  async init(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const tenants = await firstValueFrom(this.tenantService.getMyTenants());
      const persistedUuid = await this.storage.getActiveTenantUuid();

      this.tenants.set(tenants);
      await this.setActiveTenant(resolveActiveTenantUuid(tenants, persistedUuid));
    } catch (error: unknown) {
      this.tenants.set([]);
      this.activeTenantUuid.set(null);
      this.error.set(error);
    } finally {
      this.loading.set(false);
    }
  }

  async selectTenant(uuid: string): Promise<void> {
    const known = this.tenants().some((tenant) => tenant.uuid === uuid);
    if (!known) {
      return;
    }

    await this.setActiveTenant(uuid);
  }

  hasRoleInTenant(tenantUuid: string, role: TenantRole): boolean {
    return this.rolesByTenantUuid().get(tenantUuid)?.has(role) ?? false;
  }

  hasAnyRoleInTenant(tenantUuid: string, roles: readonly TenantRole[]): boolean {
    const held = this.rolesByTenantUuid().get(tenantUuid);
    if (held === undefined) {
      return false;
    }

    return roles.some((role) => held.has(role));
  }

  rolesForTenant(tenantUuid: string): readonly TenantRole[] {
    return this.tenants().find((tenant) => tenant.uuid === tenantUuid)?.roles ?? [];
  }

  async clear(): Promise<void> {
    this.tenants.set([]);
    this.activeTenantUuid.set(null);
    this.loading.set(false);
    this.error.set(null);
    await this.storage.clearActiveTenantUuid();
  }

  private async setActiveTenant(uuid: string | null): Promise<void> {
    this.activeTenantUuid.set(uuid);

    if (uuid === null) {
      await this.storage.clearActiveTenantUuid();
      return;
    }

    await this.storage.setActiveTenantUuid(uuid);
  }
}

function resolveActiveTenantUuid(
  tenants: readonly Tenant[],
  persistedUuid: string | null
): string | null {
  if (tenants.length === 0) {
    return null;
  }

  if (persistedUuid === null) {
    return tenants[0].uuid;
  }

  const stillAvailable = tenants.some((tenant) => tenant.uuid === persistedUuid);

  return stillAvailable ? persistedUuid : tenants[0].uuid;
}
